import { Button } from "@/components/ui/button";
import { FileDown, Printer } from "lucide-react";
import * as XLSX from "xlsx";
import { format } from "date-fns";

interface ReportExportProps {
  data: any[];
  filename: string;
  title: string;
  columns: { key: string; label: string }[];
}

function escapeCSV(value: any): string {
  const str = value === null || value === undefined ? "" : String(value);
  if (str.includes(",") || str.includes('"') || str.includes("\n")) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

export function ReportExport({ data, filename, title, columns }: ReportExportProps) {
  const timestamp = format(new Date(), "yyyy-MM-dd_HHmmss");
  const safeFilename = `${filename}_${timestamp}`;

  const exportCSV = () => {
    const header = columns.map((c) => escapeCSV(c.label)).join(",");
    const rows = data.map((row) =>
      columns.map((c) => escapeCSV(row[c.key])).join(",")
    );
    const csv = [header, ...rows].join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${safeFilename}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const exportExcel = () => {
    const worksheetData = data.map((row) => {
      const obj: Record<string, any> = {};
      columns.forEach((c) => {
        obj[c.label] = row[c.key];
      });
      return obj;
    });
    const worksheet = XLSX.utils.json_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, title);
    XLSX.writeFile(workbook, `${safeFilename}.xlsx`);
  };

  const handlePrint = () => {
    const printWindow = window.open("", "_blank");
    if (!printWindow) return;

    const headerCells = columns.map((c) => `<th style="padding:10px 14px;text-align:left;background:#009A44;color:#fff;font-size:12px;font-weight:600;border:1px solid #e5e7eb;">${c.label}</th>`).join("");

    const bodyRows = data
      .map((row, i) => {
        const bg = i % 2 === 0 ? "#ffffff" : "#f9fafb";
        const cells = columns
          .map((c) => `<td style="padding:8px 14px;border:1px solid #e5e7eb;font-size:13px;color:#374151;background:${bg};">${row[c.key] ?? ""}</td>`)
          .join("");
        return `<tr>${cells}</tr>`;
      })
      .join("");

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>${title}</title>
        <style>
          @page { size: A4 landscape; margin: 15mm; }
          body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; margin: 0; padding: 20px; color: #1f2937; }
          .header { text-align: center; margin-bottom: 24px; padding-bottom: 16px; border-bottom: 3px solid #009A44; }
          .header h1 { margin: 0 0 4px 0; font-size: 22px; color: #009A44; }
          .header p { margin: 0; font-size: 12px; color: #6b7280; }
          table { width: 100%; border-collapse: collapse; margin-top: 8px; }
          th { text-align: left; }
          tr:nth-child(even) td { background: #f9fafb; }
          @media print {
            body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>${title}</h1>
          <p>Generated on ${format(new Date(), "MMMM dd, yyyy 'at' HH:mm")}</p>
        </div>
        <table>
          <thead><tr>${headerCells}</tr></thead>
          <tbody>${bodyRows}</tbody>
        </table>
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 300);
  };

  return (
    <div className="flex items-center gap-2">
      <Button
        variant="outline"
        size="sm"
        onClick={exportCSV}
        className="h-8 border-gray-200 text-xs font-medium text-gray-600 hover:bg-[#009A44]/5 hover:border-[#009A44]/30 hover:text-[#009A44] gap-1.5"
      >
        <FileDown className="h-3.5 w-3.5" />
        CSV
      </Button>
      <Button
        variant="outline"
        size="sm"
        onClick={exportExcel}
        className="h-8 border-gray-200 text-xs font-medium text-gray-600 hover:bg-[#009A44]/5 hover:border-[#009A44]/30 hover:text-[#009A44] gap-1.5"
      >
        <FileDown className="h-3.5 w-3.5" />
        Excel
      </Button>
      <Button
        variant="outline"
        size="sm"
        onClick={handlePrint}
        className="h-8 border-gray-200 text-xs font-medium text-gray-600 hover:bg-[#009A44]/5 hover:border-[#009A44]/30 hover:text-[#009A44] gap-1.5"
      >
        <Printer className="h-3.5 w-3.5" />
        Print
      </Button>
    </div>
  );
}
